import { BaseClass, Utils } from "@h4x/common";

export const options: {
	debug?: boolean;
	verbose?: boolean;
} = {};

// options.verbose = true;

function TSX_DEBUG(...args: any[]) {
	if (options.verbose === true) {
		console.warn(`[H4X-TSX-DEBUG] %c${args.shift()}`, "color: #00D; font-weight: 800;", ...args); // `
	}
}

function TSX_ERROR(...args: any[]) {
	console.warn(`[H4X-TSX-ERROR] %c${args.shift()}`, "color: #D00; font-weight: 800;", ...args); // `
}

export class VNode<P = any> {
	public type: (new () => Component<P>) | string;
	public children: ComponentChild[];
	public props: P;
	public key?: string | number;
}

export type ComponentChild = VNode | string | number | null;
export type ComponentChildren = ComponentChild[] | ComponentChild;

export interface Attributes<T> {
	key?: string | number | any;
	ref?: (instance: T) => void;
}


export type ComponentProps<P, RefType = any> = Readonly<Attributes<RefType> & P>;


export function h<P>(node: (new () => Component<P>), props: Attributes<any> & P | null, ...children: ComponentChildren[]): VNode<P>;
export function h(node: string, props: JSX.HTMLAttributes<any> & JSX.SVGAttributes & Record<string, any> | null, ...children: ComponentChildren[]): VNode;
export function h<P>(type: (new () => Component<P>) | string, props: any, ...children: ComponentChildren[]) {
	let vnodes: ComponentChild[] = [];

	parseChildren(typeof type !== "function", children, vnodes);

	let p = new VNode();
	p.type = type;
	p.children = vnodes;
	if (props !== null) {
		p.props = props;
		p.key = props.key;
	}

	return p;
}

function parseChildren(simple: boolean, children: ComponentChildren[], output: ComponentChild[], lastSimple = false) {
	for (let i = 0; i < children.length; i++) {
		let child = children[i];
		if (Array.isArray(child)) {
			lastSimple = parseChildren(simple, child, output, lastSimple);
		} else {
			if (typeof child === "boolean") { child = null; }
			if (simple) {
				if (lastSimple) {
					if (child === undefined || child === null) {
						continue;
					} else if (typeof child === "number") {
						output[output.length - 1] += "" + child;
					} else if (typeof child === "string") {
						output[output.length - 1] += child;
					} else {
						lastSimple = false;
						output.push(child as ComponentChild);
					}
				} else {
					output.push(child as ComponentChild);
				}
			} else {
				output.push(child as ComponentChild);
			}
		}
	}
	return lastSimple;
}

export function renderClass(Class: (new () => Component<any>), parent: Element | Document, before?: Node | number): Element | undefined {
	return render(h(Class, null, []), parent, before);
}

export function render(vnode: ComponentChild, parent: Element | Document, before?: Node | number): Element | undefined {
	let rendered = TSX.prerender(vnode);
	rendered.attach(parent, before);
	if (parent instanceof Element && parent.isConnected) {
		if (parent.id === "app") {
			parent.classList.add("haxt");
		}
	}
	TSX.roots.set(rendered.element, rendered);
	return rendered.element;
}

export const $base = Symbol.for("$TSX_base");

const $updateProps = Symbol.for("$TSX_internal_UpdateProps");
const $update = Symbol.for("$TSX_internal_Update");
const $init = Symbol.for("$TSX_internal_Init");
const $attached = Symbol.for("$TSX_internal_Attached");
const $detached = Symbol.for("$TSX_internal_Detached");
const $destroy = Symbol.for("$TSX_internal_Destroy");

const $class = Symbol.for("$TSX_internal_prop_class");

let updateSchedule = new Set<{ [$update]: () => void; }>();

export namespace TSX {
	let interval: NodeJS.Timer | undefined;

	export let roots = new WeakMap<Element, TSX.Rendered>();

	export function configureInterval(fps: number) {
		if (interval !== undefined) { clearInterval(interval); }
		interval = setInterval(() => {
			let updating = updateSchedule;
			updateSchedule = new Set<Component<any>>();
			updating.forEach((component) => {
				try {
					component[$update]();
				} catch (err) {
					console.error("update", err);
				}
			});
		}, 1000 / fps); // 20 fps
	}

	configureInterval(20);

	export type Props<T> = T extends Component<infer R> ? R : unknown;

	export class Rendered<T extends VComponent<any> | VDomNode = VComponent<any> | VDomNode> {
		private __attached = false; // TODO change to #attached, fix rollup errors
		public get attached() { return this.__attached; }

		constructor(public readonly vnode: T, public readonly element: Element) { }

		public attach(parent: Element | Document, before?: Node | number) {
			if (this.__attached === true) {
				if (this.vnode instanceof VComponent) {
					// this.vnode.onDetached();
				}
			}

			if (before !== undefined) {
				if (typeof (before) === "number") {
					if (before <= 0) {
						parent.prepend(this.element);
					} else if (before >= parent.children.length) {
						parent.append(this.element);
					} else {
						parent.insertBefore(this.element, parent.children[before]);
					}
				} else {
					parent.insertBefore(this.element, before);
				}
			} else {
				parent.append(this.element);
			}

			if (this.__attached === false) {
				if (this.vnode instanceof VComponent) {
					this.vnode.onAttached();
					this.__attached = true;
				}
			}
			// else onMoved?
		}

		public detach() {
			if (this.__attached === true) {
				this.element.remove();
				if (this.vnode instanceof VComponent) {
					this.vnode.onDetached();
				}
				this.__attached = false;
			}
		}

		/** @deprecated Internal */
		public destroy() {
			if (this.__attached === true) {
				this.element.remove();
				this.vnode.destroy();
				this.__attached = false;
			}
		}
	}

	export type RenderedComponent<T extends Component<any>> = Rendered<VComponent<T>>;
	export type RenderedElement = Rendered<VDomElement | VComponent<any>>;

	export function prerender(vnode: VNode<any>): RenderedElement;
	export function prerender(vnode: string | number | null): Rendered<VDomNode>;
	export function prerender(vnode: ComponentChild): Rendered;
	export function prerender(vnode: ComponentChild) {
		let rendered = renderNode(vnode);
		if (rendered === undefined) {
			throw ("Couldn't render, got undefined!");
		}
		let element: Element = (rendered as any).node;

		return new Rendered(rendered, element);
	}

	export function prerenderComponent<T extends Component<any>>(vnode: ComponentChild): RenderedComponent<T> {
		if (!(vnode instanceof VNode) || typeof (vnode.type) === "string") { throw ("Couldn't prerenderComponent, not a Component!"); }
		let rendered = renderNode(vnode);
		if (rendered === undefined) {
			throw ("Couldn't render, got undefined!");
		}
		let element: Element = (rendered as any).node;

		return new Rendered(rendered, element) as RenderedComponent<T>;
	}

	export class Renderer<T extends (...args: any) => JSX.Element = () => JSX.Element> {
		private __rendered = false;
		public get rendered() { return this.__rendered; }

		public readonly vnode: VComponent<any> | VDomNode;
		public readonly element: Element;

		private constructor(private component: Component<any>, private fn: T, private args: FunctionArgs<T>) {
		}

		public update() {
			if (this.__rendered === true) {
				updateSchedule.add(this);
			}
		}

		public forceInstantUpdate() {
			immediateUpdate(this);
		}

		private _persistent = false;
		public get persistent() {
			return this._persistent;
		}
		private render(persistent?: boolean) {
			let rendered = this.fn.call(this.component, ...this.args as any);
			if (persistent !== undefined) {
				this._persistent = persistent;
			}
			return rendered;
		}

		public [$update]() {
			if (this.__rendered === true) {
				this.vnode.update(this.render());
			}
		}


		/** @Internal */
		public static renderNode(renderer: Renderer, persistent: boolean) {
			renderer.__rendered = true;
			return renderer.render(persistent);
		}

		/** @Internal */
		public static attachNode(renderer: Renderer, vdom: VDomElement | VComponent<Component<any>>) {
			(renderer.vnode as any) = vdom;
			(renderer.element as any) = vdom.node;
		}

		/** @Internal */
		public static detachNode(renderer: Renderer) {
			if (renderer.persistent === false) {
				(renderer.vnode as any) = undefined;
				(renderer.element as any) = undefined;
			}
		}

		public static create<T extends (...args: any) => JSX.Element>(component: Component<any>, fn: T, args: FunctionArgs<T>): Renderer {
			return new Renderer(component, fn, args);
		}
	}

	// -------------------------------------------------------------------------------------------------------------- //

	const SVGElements: { [K: string]: boolean | undefined; } = {
		// common
		svg: true, g: true, foreignObject: true,
		path: true, text: true, textPath: true,
		circle: true, ellipse: true, line: true, polygon: true, polyline: true, rect: true,
		// other
		animate: true, clipPath: true, defs: true, feBlend: true, feColorMatrix: true, feComponentTransfer: true,
		feComposite: true, feConvolveMatrix: true, feDiffuseLighting: true, feDisplacementMap: true, feFlood: true,
		feGaussianBlur: true, feImage: true, feMerge: true, feMergeNode: true, feMorphology: true, feOffset: true,
		feSpecularLighting: true, feTile: true, feTurbulence: true, filter: true, image: true, linearGradient: true,
		marker: true, mask: true, pattern: true, radialGradient: true, stop: true, symbol: true, tspan: true, use: true
	};

	function renderNode(vnode: VNode<any>): VDomElement | VComponent<any>;
	function renderNode(vnode: string | number | null): VDomNode | undefined;
	function renderNode(vnode: ComponentChild): VDomNode | VComponent<any> | undefined;
	function renderNode(vnode: ComponentChild): VDomNode | VComponent<any> | undefined {
		if (vnode instanceof VNode) {
			let vdom;

			let renderer;
			if (vnode.type === "inline") {
				renderer = vnode.props.renderer;
				if (renderer instanceof Renderer) {
					let inlined = Renderer.renderNode(renderer, vnode.props.persistent);
					if (inlined instanceof VNode) {
						vnode = inlined;
					} else {
						throw new Error("Invalid inline render!");
					}
				} else {
					throw new Error("Inline missing Renderer!");
				}
			}

			let type = vnode.type;
			if (typeof (type) === "string") {
				vdom = new VDomElement();

				if (SVGElements[type] === true) {
					vdom.init(document.createElementNS("http://www.w3.org/2000/svg", type), vnode.key);
				} else {
					vdom.init(document.createElement(type), vnode.key);
				}
				vdom.applyAttributes(vnode.props);

				if (vnode.type === "raw" || vnode.props?.raw === true) {
					if (vnode.children.length > 0) {
						TSX_ERROR("[VDOM] Raw nodes shouldn't have children defined in VDOM!");
					}
				} else {
					for (const child of vnode.children) {
						if (child instanceof VNode) {
							let rendered = renderNode(child);
							vdom.addChild(rendered);
						} else {
							let rendered = renderNode(child);
							if (rendered !== undefined) {
								vdom.addChild(rendered);
							}
						}
					}
				}
			} else {
				vdom = new VComponent(type, vnode.props, vnode.children);
			}

			if (renderer) {
				vdom.attachRenderer(renderer);
				Renderer.attachNode(renderer, vdom);
			}

			return vdom;
		} else if (typeof (vnode) === "string") {
			return new VDomText("" + vnode);
		} else if (typeof (vnode) === "number") {
			return new VDomText("" + vnode);
		} else if (vnode === undefined || vnode === null) { // FIXME remove null! throw?
			return undefined;
		} else if (Array.isArray(vnode)) {
			return undefined;
		}

		throw new Error("Invalid node type!");
	}

	abstract class VDom {
		public readonly key?: string | number;
		public isActive = true;
		public renderer?: Renderer;

		public isCompatible(vnode: ComponentChild): boolean {
			throw new Error("Invalid isCompatible!");
		}

		public abstract update(vnode: ComponentChild): void;

		attachRenderer(renderer: Renderer) {
			this.renderer = renderer;
		}
	}

	export abstract class VDomNode extends VDom {
		protected nodeName: string;
		public node: Node;
		public mappedChildren = new Map<string | number, (VDomNode | VComponent<any>)>();
		public inlined = new Set<Renderer>();
		public isPersistent = false;

		get typeName() {
			return "VDomNode";
		}

		constructor() {
			super();
		}

		public init(node: Node, key: string | number | undefined) {
			this.node = node;
			(this as any).key = key;

			this.nodeName = this.node.nodeName.toLowerCase();
			return this;
		}

		public abstract destroy(): void;
	}

	const attributeHandlers: { [K: string]: (node: Element, old: any, target: any) => void } = {
		class: (node, old: { [K: string]: boolean } | undefined | null, target: { [K: string]: boolean } | undefined | null) => {
			if (target === undefined || target === null) {
				node.removeAttribute("class");
			} else if (typeof (target) === "object") {
				if (options.debug === true) {
					if (Array.isArray(target)) {
						TSX_DEBUG("Got invalid attribute for class, of type array, and value " + target + "");
					}
					if (Array.isArray(old)) {
						TSX_DEBUG("Got invalid attribute for class, of type array, and value " + target + "");
					}
				}
				let classList = node.classList;

				if (old !== undefined && old !== null) {
					// clear old values
					for (const name in old) {
						if (old[name] === true) {
							const newValue = target[name];
							if (newValue === undefined || newValue === false) {
								classList.remove(name);
							}
						}
					}

					// apply/replace values
					for (const name in target) {
						const oldValue = old[name];
						const newValue = target[name];
						if (newValue === true && (oldValue === undefined || oldValue === false)) {
							classList.add(name);
						}
					}
				} else {
					// apply new values
					for (const name in target) {
						if (target[name] === true) {
							classList.add(name);
						}
					}
				}
			} else {
				TSX_DEBUG("Got invalid attribute for class, of type " + (typeof (target)) + ", and value " + target + "");
			}
		},
		ref: (node, old, target) => { if (old !== target) { if (old) { old(null); } if (target) { target(node); } } },
		style: (node, old, target) => {
			let style = (node as any).style;
			if (style instanceof CSSStyleDeclaration) {
				if (!target || typeof target === "string") {
					style.cssText = target || "";
				} else if (typeof old === "string") {
					style.cssText = "";
				}
				if (target && typeof target === "object") {
					if (typeof old !== "string") {
						for (let i in old) {
							if (!(i in target)) { (style as any)[i] = ""; }
						}
					}
					for (let i in target) {
						if (target.hasOwnProperty(i)) {
							if (i === "flex") {
								style[i] = target[i];
							} else {
								(style as any)[i] = typeof target[i] === "number" ? (target[i] + "px") : target[i];
							}
						}
					}
				}
			}
		},
		dangerouslySetInnerHTML: (node, _old, target) => { if (target) { node.innerHTML = target.__html || ""; } },

		children: () => { /* noop */ },
		innerHTML: () => { /* noop */ },
		raw: () => { /* noop */ },
	};

	attributeHandlers.className = attributeHandlers.class;

	export class VDomElement extends VDomNode {

		public override readonly node: Element;
		private [$class]: { [K: string]: boolean };

		private props: any = {};
		public children: (VDomNode | VComponent<any>)[] = [];

		public destroy() {
			for (const child of this.children) { child.destroy(); }
			this.applyAttributes(undefined);
			(this as any).node = undefined!;
			this.props = undefined!;
		}

		override get typeName() {
			return "VDomElement->" + this.nodeName;
		}

		public applyAttributes(target: any): any {
			let dom = this.node as Element;

			if (!target) {
				// remove all props
				for (let name in this.props) {
					if (this.props.hasOwnProperty(name)) {
						let old = name === "class" ? this[$class] : this.props[name];
						this.applyAttribute(dom, name, old, undefined);
						if (name === "class") {
							this[$class] = undefined!;
						}
					}
				}

				target = {};
			} else {
				// remove missing props
				for (let name in this.props) {
					if (target[name] == null && this.props[name] != null) {
						let old = name === "class" ? this[$class] : this.props[name];
						this.applyAttribute(dom, name, old, undefined);
						if (name === "class") {
							this[$class] = undefined!;
						}
					}
				}

				// add new props and update existing ones
				for (let name in target) {
					if (target.hasOwnProperty(name)) {
						let old = this.props[name];

						// get value/checked directly from the dom
						if (name === "value" || name === "checked") {
							old = (dom as HTMLInputElement)[name];
						}

						let value = target[name];
						if (value !== old) {
							if (name === "class") {
								if (typeof (value) === "string") {
									value = value.split(" ").filter(x => x.length > 0);
									let converted: { [K: string]: boolean } = {};
									for (const name of value) {
										converted[name] = true;
									}
									value = converted;
								} else {
									if (Array.isArray(value)) {
										let converted: { [K: string]: boolean } = {};
										for (const name of value) {
											if (typeof (name) === "string" && name.length > 0) {
												converted[name] = true;
											}
										}
										value = converted;
									}
								}

								this.applyAttribute(dom, name, this[$class], value);
								this[$class] = value as { [K: string]: boolean };
							} else {
								this.applyAttribute(dom, name, old, value);
							}
						}
					}
				}
			}

			this.props = target;
		}

		private listeners: any = {};
		get eventHandler() {
			let self = this;
			const fnBound = function (this: Node, e: Event) {
				if (self.listeners[e.type] !== undefined) {
					self.listeners[e.type](e, this);
				}
			};
			Object.defineProperty(this, "eventHandler", { value: fnBound, configurable: true, writable: true });
			return fnBound;
		}

		private applyAttribute(node: Element, name: string, old: any, target: any) {
			let handler = attributeHandlers[name];

			if (handler !== undefined) {
				return handler(node, old, target);
			}

			if (name.startsWith("on")) {
				name = name.toLowerCase().substring(2);
				if (target) {
					if (!old) { node.addEventListener(name, this.eventHandler, false); }
				} else {
					node.removeEventListener(name, this.eventHandler, false);
				}
				this.listeners[name] = target;
			} else if (name.startsWith("capture")) {
				name = name.toLowerCase().substring(7);
				if (target) {
					if (!old) { node.addEventListener(name, this.eventHandler, true); }
				} else {
					node.removeEventListener(name, this.eventHandler, true);
				}
				this.listeners[name] = target;
			} else if (
				node.namespaceURI !== "http://www.w3.org/2000/svg"
				&& name !== "href" && name !== "list" && name !== "type" && name !== "tabIndex" && name !== "download"
				&& name in node) {
				try { (node as any)[name] = (target == null) ? "" : target; } catch (e) { }
				if (target == null) { node.removeAttribute(name); }
			} else {
				if (target == null) {
					node.removeAttribute(name);
				} else if (typeof target !== "function" && typeof target !== "object") {
					node.setAttribute(name, target);
				}
			}
		}

		public update(vnode: VNode) {
			if (vnode instanceof VNode === false) { throw new Error("Invalid node type!"); }

			let type = vnode.type;
			if (typeof (type) === "string") {
				(this as any as VDomElement).applyAttributes(vnode.props);
				this.updateChildren(vnode);
			} else {
				throw new Error("Invalid node type!");
			}
		}

		public addChild(vdom: VDomNode | VComponent<any>) {
			this.children.push(vdom);
			if (vdom.key !== undefined) {
				this.mappedChildren.set(vdom.key, vdom);
			}
			this.node.appendChild(vdom.node);

			if (vdom instanceof VComponent) {
				vdom.onAttached();
			}

			if (vdom.renderer) {
				this.inlined.add(vdom.renderer);
			}
		}

		public updateChildren(vnode: VNode) {
			if (this.nodeName === "raw" || vnode.props?.raw === true) {
				if (this.children.length > 0) {
					TSX_ERROR("[VDOM] Raw nodes shouldn't have children defined in VDOM!");
				}
				return;
			}

			for (const child of this.children) {
				child.isActive = false;
			}

			let updatedChildren: (VDomNode | VComponent<any>)[] = [];
			let updatedInline = new Set<Renderer>();

			for (const vChild of vnode.children) {
				let current;

				if (vChild instanceof VNode && vChild.type === "inline") {
					let renderer = vChild.props.renderer;
					if (renderer instanceof Renderer) {
						updatedInline.add(renderer);
						current = renderer.vnode;
					} else {
						throw new Error("Inline missing Renderer!");
					}

					if (current !== undefined) {
						updatedChildren.push(current);
						current.isActive = true;
						continue;
					}
				} else if (vChild instanceof VNode && vChild.key !== undefined) {
					current = this.mappedChildren.get(vChild.key);
					if (current !== undefined && current.isCompatible(vChild) === false) {
						TSX_ERROR(`[VNode] Incompatible component with key ${JSON.stringify(vChild.key)}, got ${current.typeName}, expected ${(vChild.type as any).name}!`);// `
						let index = this.children.indexOf(current);
						if (index > -1) { this.children.splice(index, 1); this.node.removeChild(current.node); }
						current.destroy();
						current = undefined;
						this.mappedChildren.delete(vChild.key);
					}
				} else {
					let c = 0; // TODO optimize start
					while (c < this.children.length) {
						let child = this.children[c++];
						if (child.renderer !== undefined) { continue; } // skip children with a renderer attached
						if (child.key !== undefined || child.isActive === true || child.isCompatible(vChild) === false) {
							continue;
						}
						current = child;
						break;
					}
				}


				if (current) {
					updatedChildren.push(current);
					current.isActive = true; // mark it as active
					(current as VDom).update(vChild);
				} else {
					let newChild = renderNode(vChild);
					if (newChild) {
						updatedChildren.push(newChild);
					}
				}
			}

			for (const inlined of this.inlined) {
				if (updatedInline.has(inlined) === false) {
					let child = inlined.vnode;
					if (child.isPersistent === false) {
						if (child.key !== undefined) {
							this.mappedChildren.delete(child.key);
						}
					}
					let index = this.children.findIndex(x => x === child);
					this.node.removeChild(child.node);
					this.children[index] = undefined!;
					Renderer.detachNode(inlined);
					if (inlined.persistent === false) {
						child.destroy();
					}
				}
			}
			this.inlined = updatedInline;

			for (let c = 0; c < this.children.length; c++) {
				const child = this.children[c];
				if (child !== undefined && child.isActive === false) {
					if (child.isPersistent === false) {
						if (child.key !== undefined) {
							this.mappedChildren.delete(child.key);
						}
						this.node.removeChild(child.node);
						this.children[c] = undefined!;
						child.destroy();
					} else {
						this.node.removeChild(child.node);
						if (child instanceof VComponent) {
							child.onDetached();
						}
						this.children[c] = undefined!;
					}
				}
			}

			let i = 0;
			let oldChildren = this.children;
			this.children = updatedChildren;
			for (const newChild of this.children) {
				let oldChild;
				while (oldChild === undefined && i < oldChildren.length) {
					oldChild = oldChildren[i++];
				}
				let existing = false;
				if (oldChild) {
					if (oldChild !== newChild) {
						this.node.insertBefore(newChild.node, oldChild.node);
						for (let c = i; c < oldChildren.length; c++) {
							if (oldChildren[c] === newChild) {
								oldChildren[c] = undefined!;
								existing = true;
								break;
							}
						}
						i--; // ???
					} else {
						existing = true;
					}
				} else {
					this.node.appendChild(newChild.node);
				}

				if (existing === false) {

					if (newChild.key !== undefined) {
						if (options.debug === true) {
							let current = this.mappedChildren.get(newChild.key);
							if (current !== undefined && current.isPersistent === false) {
								TSX_ERROR(`[VDOM] Key ${JSON.stringify(newChild.key)} already exists while adding a new key!`);
							}
						}
						this.mappedChildren.set(newChild.key, newChild);
					}
					if (newChild instanceof VComponent) {
						newChild.onAttached();
					}
				}
			}

			if (options.debug === true) {
				let domNodes = Array.from(this.node.childNodes.values());
				let currentNodes = this.children.map(x => x.node);
				if (Utils.equals(domNodes, currentNodes) === false) {
					TSX_ERROR("[VDOM] Update failed, child nodes don't match!");
				}
			}
		}

		public override isCompatible(vnode: ComponentChild) {
			return vnode instanceof VNode && typeof vnode.type === "string" && vnode.type.toLowerCase() === this.nodeName;
		}
	}


	class VDomText extends VDomNode {
		constructor(text: string) {
			super();
			this.currentText = text;
			this.init(document.createTextNode(text), undefined);
		}

		public destroy() {
			this.node = undefined!;
		}

		override get typeName() {
			return "VDomText";
		}

		private currentText = "";
		public setText(text: string) {
			if (this.currentText !== text) {
				this.currentText = text;
				(<Text>this.node).nodeValue = text;
			}
		}

		public update(vnode: string | number) {
			if (typeof (vnode) === "string") {
				this.setText(vnode);
			} else if (typeof (vnode) === "number") {
				this.setText("" + vnode);
			} else {
				throw new Error("Invalid node type!");
			}
		}

		public override isCompatible(vnode: ComponentChild) {
			return typeof vnode === "string" || typeof vnode === "number";
		}
	}

	export class VComponent<T extends Component<any>> extends VDom {
		public instance: T;
		public vdom: VDomElement | VComponent<any>;

		public isPersistent = false;

		get node(): Node {
			return this.vdom.node;
		}

		constructor(type: new () => T, props: any, children: ComponentChild[]) {
			super();

			if (type.prototype === undefined || type.prototype.render === undefined) {
				throw new Error("Component need to have a render function!");
			}
			let instance: T = new type();
			if (!(type.prototype instanceof Component)) {
				throw new Error("Components must extend the Component class!");
			}

			this.instance = instance;
			instance[$base] = this;
			instance[$init](props, children);
			this.vdom = renderNode(instance.render());

			if (props !== undefined) {
				if ((props as any).key) {
					(this as any).key = (props as any).key;
				}
				if ((props as any).persistent) {
					this.isPersistent = true;
				}
				if (props.ref) {
					props.ref(instance);
				}
			}
		}

		public destroy() {
			this.vdom.destroy();
			this.onDetached();
			this.instance[$destroy]();
			this.instance[$base] = undefined!;
		}

		public onAttached() {
			this.instance[$attached]();
		}
		public onDetached() {
			this.instance[$detached]();
		}

		get typeName() {
			return "VDomComponent->" + this.instance.constructor.name;
		}

		public componentUpdate(vnode: VNode) {
			if (this.vdom.isCompatible(vnode)) {
				TSX_DEBUG("Compatible", this, vnode);
				this.vdom.update(vnode);
			} else {
				// rerender
				TSX_DEBUG("Incompatible", this, vnode);
				let oldNode = this.node;
				this.vdom = renderNode(vnode);
				// debugger;
				oldNode.parentNode!.replaceChild(this.node, oldNode);
			}
		}


		public update(vnode: VNode) {
			TSX_DEBUG("VComponent update");
			if (vnode instanceof VNode === false) { throw new Error("Invalid node type!"); }

			let type = vnode.type;
			if (typeof (type) === "string") {
				throw new Error("Invalid node type!");
			} else {
				let instance = this.instance;
				if (instance === undefined) {
					throw new Error("Missing component instance!");
				}
				instance[$updateProps](vnode.props, vnode.children);
			}
		}

		public internalUpdateProps(props: any) {
			TSX_DEBUG("VComponent internalUpdateProps");
			let instance = this.instance;
			if (instance === undefined) {
				throw new Error("Missing component instance!");
			}
			instance[$updateProps](props, instance.children);
		}

		public override isCompatible(vnode: ComponentChild) {
			if (vnode instanceof VNode && typeof vnode.type === "function") {
				return this.instance.constructor === vnode.type;
			}
			return false;
		}
	}

	export function getPath(element: HTMLElement | null): { components: Component<any>[]; element: HTMLElement; }[] {
		let path = []; // html -> body -> ... -> element
		while (element) {
			path.unshift(element);
			element = element.parentElement;
		}

		let mapped = [];
		let current: TSX.VComponent<any> | TSX.VDomNode | Fragment.AbstractList<Fragment<any>> | undefined = undefined;
		for (let element of path) {
			let components: Component<any>[] = [];
			if (current === undefined) {
				let rendered = TSX.roots.get(element);
				if (rendered !== undefined) {
					current = rendered.vnode;
				}
			}

			while (current !== undefined) {
				if (current instanceof TSX.VComponent) {
					components.push(current.instance);
					if (current.instance instanceof Fragment.Container) {
						current = current.instance[Fragment.$fragments];
					} else {
						current = current.vdom;
					}
					continue;
				} else if (current instanceof TSX.VDomElement) {
					let found = false;
					for (let child of current.children) {
						if (child instanceof TSX.VDomNode && child.node === element) {
							current = child;
							found = true;
							break;
						} else if (child instanceof TSX.VComponent && child.node === element) {
							current = child;
							found = true;
							break;
						}
					}
					if (found) {
						continue;
					}
				} else if (current instanceof Fragment.AbstractList) {
					let found = false;
					for (let fragment of current) {
						if (fragment.element === element) {
							current = fragment.vnode;
							found = true;
							break;
						}
					}
					if (found) {
						continue;
					}
				}
				break;
			}

			mapped.push({ components, element });
		}

		return mapped;
	}
}


let immediateSchedule = new Set<{ [$update]: () => void; }>();
let immediate = false;

function immediateUpdate(component: { [$update]: () => void; }) {
	immediateSchedule.add(component);
	if (immediate === false) {
		immediate = true;
		Utils.nextTick().then(() => {
			let updating = immediateSchedule;
			immediateSchedule = new Set<{ [$update]: () => void; }>();
			updating.forEach((component) => {
				try {
					component[$update]();
				} catch (err) {
					console.error("immediateUpdate", err);
				}
			});
			immediate = false;
		});
	}
}

type FunctionArgs<T extends (...args: any) => any> = T extends (...args: infer P) => any ? P : never;

export abstract class Component<P> extends BaseClass {
	public static readonly Children = Symbol.for("$TSX_Children");

	private static _REF = 1;
	public readonly REF = "TSX:" + (Component._REF++);

	public [$base]: TSX.VComponent<this>;
	private updateScheduled = false;
	private internalUpdating = false;
	private internalDestroyed = false;

	public props: ComponentProps<P, this>; // TODO ref returns an instance of the Component
	public children: ComponentChild[];

	constructor() {
		super();
		TSX_DEBUG("created " + this.constructor.name);
	}

	protected createRenderer<T extends (...args: any) => JSX.Element>(fn: T, ...args: FunctionArgs<T>): TSX.Renderer {
		return TSX.Renderer.create(this, fn, args);
	}

	public abstract render(): VNode<this>;

	public [$init](props: ComponentProps<P, any>, children: ComponentChild[]) {
		if (props === undefined) { props = {} as any; }
		this.props = props;
		this.children = children;
		if ((this as any).__init) {
			(this as any).__init();
		}
		this.init();
		if ((this as any).__propsChanged) {
			(this as any).__propsChanged({}, props);
		}
	}
	protected init() { }

	public [$destroy]() {
		this.internalDestroyed = true;
		if ((this as any).__destroy) {
			(this as any).__destroy();
		}
		this.destroy();
	}
	protected destroy() { }

	public onAttached() { }
	public onDetached() { }

	public [$attached]() { this.onAttached(); TSX_DEBUG("Attached " + this.REF); }
	public [$detached]() { this.onDetached(); TSX_DEBUG("Detached " + this.REF); }

	public [$update]() {
		if (this[$base] === undefined) {
			// ignore the component could have been deleted by the parent
		} else {
			this.preUpdate();
			this[$base].componentUpdate(this.render());
			this.postUpdate();
		}
	}

	protected update() {
		if (this[$base] === undefined) {
			if (this.internalDestroyed) {
				TSX_ERROR("Component dom is undefined when trying to schedule an update, the component was been already destroyed!");
			} else {
				TSX_ERROR("Component dom is undefined when trying to schedule an update!");
			}
		}

		if (this.internalUpdating) {
			this.updateScheduled = true;
		} else {
			updateSchedule.add(this);
		}
	}

	protected forceInstantUpdate() {
		if (this[$base] === undefined) {
			if (this.internalDestroyed) {
				TSX_ERROR("Component dom is undefined when trying to schedule an update, the component was been already destroyed!");
			} else {
				TSX_ERROR("Component dom is undefined when trying to schedule an update!");
			}
		}

		if (this.internalUpdating) {
			this.updateScheduled = true;
		} else {
			immediateUpdate(this);
		}
	}

	protected preUpdate() { }

	protected postUpdate() { }

	protected receivedProps(props: ComponentProps<P, this>, children: ComponentChild[]) {
		TSX_DEBUG("receivedProps " + this.constructor.name);
		this.willReceiveProps(props, children);
		let oldProps = this.props;
		this.props = props;
		this.children = children;
		if ((this as any).__propsChanged) {
			(this as any).__propsChanged(oldProps, props);
		}
	}

	protected willReceiveProps(props: ComponentProps<P, this>, children: ComponentChild[]) { }

	public [$updateProps](props: ComponentProps<P, this>, children: ComponentChild[]) {
		if (props === undefined) { props = {} as any; }
		TSX_DEBUG("internalUpdateProps " + this.constructor.name);
		this.internalUpdating = true;

		this.receivedProps(props, children);

		// if one of the update functions is called in receivedProps/willReceiveProps => this.updateScheduled = true;
		// this makes child updates instant when they are caused by props propagation
		this.internalUpdating = false;
		if (this.updateScheduled) {
			this[$update]();
			this.updateScheduled = false;
		}
	}
}

export namespace Component { }

import { Fragment } from "../Components/Fragment";